import React, { useState, useEffect } from "react";
import { useNavigate, Link } from 'react-router-dom';
import Navbar from "./Navbar";
import Sidebar from "./Sidebar";
import "./Upload.css";
import { BASE_URL } from "./Constants";
import useAuth from './useAuth';
import * as XLSX from "xlsx"; // Importing XLSX for export functionality

const Employee = () => {
  const [question, setQuestion] = useState([]);
  const [filteredQuestions, setFilteredQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20); // Set default to 20 items per page
  const [searchQuery, setSearchQuery] = useState("");
  const [EmpCode, setEmpCode] = useState("");

  const [selectedDept, setSelectedDept] = useState(""); // To filter by department
  useAuth();

  // Fetch questions from API
  useEffect(() => {
    const fetchsetQuestion = async () => {
      const token = localStorage.getItem("authToken");
      try {
        const response = await fetch(`${BASE_URL}users`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (!response.ok) throw new Error("Failed to fetch questions");
        const data = await response.json();
        setQuestion(data.viva || []); // Ensure you are using the correct key here
        setEmpCode(data.emp_code);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };
    fetchsetQuestion();
  }, []);

  // Filter questions based on search and department
  useEffect(() => {
    let filtered = question;

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(
        (q) =>
          (q.emp_code?.toLowerCase().includes(searchQuery.toLowerCase()) || '') || // empcode included in search
          (q.comp_name?.toLowerCase().includes(searchQuery.toLowerCase()) || '') || // compname included in search
          (q.dept?.toLowerCase().includes(searchQuery.toLowerCase()) || '') ||
          (q.question?.toLowerCase().includes(searchQuery.toLowerCase()) || '') ||
          (q.rating?.toLowerCase().includes(searchQuery.toLowerCase()) || '') ||
          (q.created_by?.toLowerCase().includes(searchQuery.toLowerCase()) || '')
      );
    }

    // Filter by department if selected
    if (selectedDept) {
      filtered = filtered.filter((q) => q.dept === selectedDept);
    }

    setFilteredQuestions(filtered);
  }, [searchQuery, selectedDept, question]);

  // Paginate questions based on the current page and items per page
  const paginateQuestions = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredQuestions.slice(startIndex, endIndex);
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // Reset to the first page when search changes
  };

  // Handle department filter change
  const handleDeptChange = (e) => {
    setSelectedDept(e.target.value);
    setCurrentPage(1); // Reset to the first page when department filter changes
  };

  // Handle rows per page change
  const handleRowsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1); // Reset to the first page when rows per page changes
  };

  // Export to Excel functionality
  const exportToExcel = () => {
    const ws = XLSX.utils.json_to_sheet(filteredQuestions);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Employee Data");
    XLSX.writeFile(wb, "employee_data.xlsx");
  };

  // Handle reset password with confirmation popup
  const handleResetPassword = async (empCode) => {
    const confirmed = window.confirm("Are you sure you want to reset the password for this employee?");
    if (confirmed) {
      const token = localStorage.getItem("authToken");
      try {
        // Make the API request to reset the password
        const response = await fetch(`${BASE_URL}reset_password`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ empCode }), // Send the empCode in the request body
        });

        if (!response.status) {
          throw new Error("Failed to reset password");
        }

        const data = await response.json();
        alert(`Password for employee ${empCode} has been successfully reset!`);

        // You can refresh the employee data or handle state updates here if needed
      } catch (err) {
        alert(`Error: ${err.message}`);
      }
    }
  };

  return (
    <div className="container-scroller">
      <Navbar />
      <div className="container-fluid page-body-wrapper">
        <Sidebar />

        <div className="main-panel">
          <div className="content-wrapper">
            {/* Table Section */}
            <div className="row">
              <div className="col-lg-12 grid-margin stretch-card">
                <div className="card">
                  <div className="card-body">
                    <h4 className="card-title">Employee List</h4>

                    {/* Filters Section */}
                    <div className="mb-3">
                      <div className="row">
                        <div className="col-lg-1">
                          <select
                            className="form-control input-font"
                            style={{padding:"0px 10px"}}
                            value={itemsPerPage}
                            onChange={handleRowsPerPageChange}
                          >
                            <option value={10}>10</option>
                            <option value={20}>20</option> {/* Set 20 as default */}
                            <option value={30}>30</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                          </select>
                        </div>
                        <div className="col-lg-5">
                          <select
                            className="form-control input-font"
                            value={selectedDept}
                            style={{padding:"0px 10px"}}
                            onChange={handleDeptChange}
                          >
                            <option value="">All Departments</option>
                            {Array.from(new Set(question.map((q) => q.dept))).map((dept) => (
                              <option key={dept} value={dept}>
                                {dept}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="col-lg-5">
                          <input
                            type="text"
                            className="form-control input-font"
                            placeholder="Search by Employee Code, Company, Department, etc."
                            value={searchQuery}
                            onChange={handleSearchChange}
                          />
                        </div>

                        <div className="col-lg-1">
                          <button
                          className="btn btn-success mb-3 input-font"
                          style={{padding:"5px 10px"}}
                          onClick={exportToExcel}
                        >
                          Export to Excel
                        </button>
                        </div>
                      </div>
                    </div>

                    {/* Display loading or error messages */}
                    {loading && <p>Loading...</p>}
                    {error && <p style={{ color: "red" }}>{error}</p>}

                    {/* Render Paginated Questions Table */}
                    {!loading && !error && filteredQuestions.length > 0 && (
                      <>
                        {/* <button
                          className="btn btn-success mb-3"
                          onClick={exportToExcel}
                        >
                          Export to Excel
                        </button> */}
                        <table className="table" style={{ fontSize: "13px" }}>
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>Employee Code</th>
                              <th>Complete Name</th>
                              <th>Dept Name</th>
                              {/* <th>Reset Password</th> */}
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {paginateQuestions().map((questionItem, index) => (
                              <tr key={questionItem.id}>
                                <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                                <td>{questionItem.emp_code}</td>
                                <td>{questionItem.comp_name}</td>
                                <td>{questionItem.dept_name}</td>
                                {/* <td>
                                  <button
                                    className="btn btn-warning input-font"
                                    style = {{padding: "5px 10px",textAlign:"center"}}
                                    onClick={() => handleResetPassword(questionItem.emp_code)}
                                  >
                                    Reset Password
                                  </button>
                                </td> */}
                                <td>
                                <Link to={`/edit_employee/${questionItem.emp_code}`}>
                                                                         
                                                                         <i className="fa fa-edit" style={{ fontSize: '12px', color: 'green' }}></i>
                                                                     </Link>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
<br/>
                        {/* Pagination Controls */}
                        <div className="pagination">
                          <button
                            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}
                            className="btn btn-secondary"
                          >
                            Previous
                          </button>
                          <span className="mx-3">Page {currentPage}</span>
                          <button
                            onClick={() =>
                              setCurrentPage((prev) =>
                                Math.min(prev + 1, Math.ceil(filteredQuestions.length / itemsPerPage))
                              )
                            }
                            disabled={currentPage === Math.ceil(filteredQuestions.length / itemsPerPage)}
                            className="btn btn-secondary"
                          >
                            Next
                          </button>
                        </div>

                        {/* Rows Count Display */}
                        <div className="row-count-display">
                          <p>
                            Showing {(currentPage - 1) * itemsPerPage + 1}-
                            {Math.min(currentPage * itemsPerPage, filteredQuestions.length)} of{" "}
                            {filteredQuestions.length} rows
                          </p>
                        </div>
                      </>
                    )}

                    {/* No data found message */}
                    {!loading && !error && filteredQuestions.length === 0 && (
                      <p>No questions found</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <footer className="footer">
            <span className="text-muted">© {new Date().getFullYear()} Vijaya Diagnostic Centre</span>
          </footer>
        </div>
      </div>
    </div>
  );
};

export default Employee;
